import { selectFrom, selectSingleFrom } from '../utils/dom';
import BaseController from './BaseController';

export const ELEMENT_CONTROLLER_ID = 'ElementController';
export const ELEMENT_CONTROLLER_ROOT_ATTRIBUTE = 'data-controller';

/**
 * Controller bound to an element.
 *
 * Requires a data attribute with the controller name on an element that will
 * then be the controller's root. If a root doesn't exist on the page, the
 * controller will not be initialized.
 */
export default class ElementController extends BaseController {
  /**
   * @see BaseController._baseId
   */
  static _baseId = ELEMENT_CONTROLLER_ID;

  /**
   * The controller root DOM node.
   *
   * @ignore
   * @member {?Element}
   */
  _root = null;

  /**
   * Initialize base controller.
   *
   * @ignore
   * @param {Function} controllerConstructor - The controller class being
   *   instantiated.
   * @param {?Element} rootElement - The controller's root element.
   */
  _init(controllerConstructor, rootElement) {
    this._root = rootElement;
    this._initBase(controllerConstructor);
  }

  /**
   * Get the controller's root element.
   *
   * @returns {?Element} Root element, or null if no root is set.
   */
  getRoot() {
    this._validateRoot();
    return this._root;
  }

  /**
   * Get nodes contained inside the controller root.
   *
   * @ignore
   * @param {string} selector - Node selector.
   * @param {Function} selectionFunc - Selection function.
   * @param {Element} [base] - Element to select from. Default to controller
   *   root.
   * @returns {Array.<Element> | ?Element} An array of found elements or a
   *   single found element, can be empty or null.
   */
  _getEl(selector, selectionFunc, base = this._root) {
    this._validateRoot();

    return selectionFunc(base, selector);
  }

  /**
   * Get nodes contained inside the controller root.
   *
   * @param {string} selector - Node selector. Prefer a name set with data
   *   attribute if possible.
   * @param {Element} [base] - Element to select from. Default to controller
   *   root.
   * @returns {Array.<Element>} An array of found elements, can be empty.
   */
  getNodes(selector, base) {
    return this._getEl(selector, selectFrom, base);
  }

  /**
   * Get a single node contained inside the controller root.
   *
   * @param {string} selector - Node selector. Prefer a name set with data
   *   attribute if possible.
   * @param {Element} [base] - Element to select from. Default to controller
   *   root.
   * @returns {?Element} Found element or null.
   */
  getSingleNode(selector, base) {
    return this._getEl(selector, selectSingleFrom, base);
  }

  /**
   * Throw an error if a root element isn't set.
   *
   * @ignore
   */
  _validateRoot() {
    if (!this._root) {
      throw new Error(
        `No root element set for controller '${this._controllerConstructor.name}', can't use element helpers.`,
      );
    }
  }
}
